'use client';

import React, { useEffect, useState, useRef, useCallback } from 'react';
import Image from 'next/image';
import { Download, Share, X, RotateCw, PlusSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

interface BeforeInstallPromptEvent extends Event {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const DISMISS_STORAGE_KEY = 'ekutu_pwa_prompt_dismissed_until';
const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

export function PwaProvider({ children }: { children: React.ReactNode }) {
	const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
	const [showBanner, setShowBanner] = useState(false);
	const [showIosGuide, setShowIosGuide] = useState(false);
	const [isIos, setIsIos] = useState(false);
	const [isStandalone, setIsStandalone] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [pullDistance, setPullDistance] = useState(0);

	const touchStartY = useRef(0);
	const isPulling = useRef(false);

	const checkStandalone = useCallback(() => {
		if (typeof window === 'undefined') return false;
		const isStandaloneMode =
			window.matchMedia('(display-mode: standalone)').matches ||
			(navigator as unknown as { standalone?: boolean }).standalone === true ||
			document.referrer.includes('android-app://');
		setIsStandalone(Boolean(isStandaloneMode));
		return Boolean(isStandaloneMode);
	}, []);

	const triggerRefresh = useCallback(() => {
		setIsRefreshing(true);
		toast.info('Updating & refreshing...');
		setTimeout(() => {
			window.location.reload();
		}, 300);
	}, []);

	useEffect(() => {
		// 1. Register Service Worker
		if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
			window.addEventListener('load', () => {
				navigator.serviceWorker
					.register('/sw.js')
					.catch((err) => console.debug('SW registration error:', err));
			});
		}

		// 2. Check standalone
		const standalone = checkStandalone();

		// 3. Detect iOS (iPhone, iPad, iPod, iPadOS)
		const userAgent = window.navigator.userAgent.toLowerCase();
		const isIosDevice =
			/iphone|ipad|ipod/.test(userAgent) ||
			(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
		setIsIos(isIosDevice);

		// 4. Custom event listener for manual trigger
		const handleOpenInstall = () => {
			if (checkStandalone()) {
				toast.info('Ekutu QR is already installed and running as an app.');
				return;
			}
			if (deferredPrompt) {
				void deferredPrompt.prompt().then(() => {
					if (deferredPrompt.userChoice) {
						void deferredPrompt.userChoice.then((c) => {
							if (c.outcome === 'accepted') setDeferredPrompt(null);
						});
					}
				});
			} else if (isIosDevice) {
				setShowIosGuide(true);
			} else {
				setShowBanner(true);
			}
		};

		window.addEventListener('ekutu:open-install-prompt', handleOpenInstall);

		// 5. Chromium beforeinstallprompt
		const handleBeforeInstallPrompt = (e: Event) => {
			e.preventDefault();
			setDeferredPrompt(e as BeforeInstallPromptEvent);
			const dismissedUntil = localStorage.getItem(DISMISS_STORAGE_KEY);
			if (!standalone && (!dismissedUntil || Date.now() > Number(dismissedUntil))) {
				setTimeout(() => setShowBanner(true), 1500);
			}
		};

		window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

		// If on iOS and not standalone, show banner after 1.5s
		if (isIosDevice && !standalone) {
			const dismissedUntil = localStorage.getItem(DISMISS_STORAGE_KEY);
			if (!dismissedUntil || Date.now() > Number(dismissedUntil)) {
				const t = setTimeout(() => setShowBanner(true), 1500);
				return () => clearTimeout(t);
			}
		}

		return () => {
			window.removeEventListener('ekutu:open-install-prompt', handleOpenInstall);
			window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
		};
	}, [checkStandalone, deferredPrompt]);

	// Pull-to-refresh on touch devices
	useEffect(() => {
		const handleTouchStart = (e: TouchEvent) => {
			if (window.scrollY === 0 && e.touches[0]) {
				touchStartY.current = e.touches[0].clientY;
				isPulling.current = true;
			} else {
				isPulling.current = false;
			}
		};

		const handleTouchMove = (e: TouchEvent) => {
			if (!isPulling.current || window.scrollY > 0 || !e.touches[0]) return;
			const currentY = e.touches[0].clientY;
			const diff = currentY - touchStartY.current;
			if (diff > 0) {
				setPullDistance(Math.min(diff * 0.45, 90));
			}
		};

		const handleTouchEnd = () => {
			if (!isPulling.current) return;
			isPulling.current = false;
			if (pullDistance > 65) {
				triggerRefresh();
			}
			setPullDistance(0);
		};

		window.addEventListener('touchstart', handleTouchStart, { passive: true });
		window.addEventListener('touchmove', handleTouchMove, { passive: true });
		window.addEventListener('touchend', handleTouchEnd);

		return () => {
			window.removeEventListener('touchstart', handleTouchStart);
			window.removeEventListener('touchmove', handleTouchMove);
			window.removeEventListener('touchend', handleTouchEnd);
		};
	}, [pullDistance, triggerRefresh]);

	const handleInstallClick = async () => {
		if (deferredPrompt) {
			try {
				await deferredPrompt.prompt();
				const choiceResult = await deferredPrompt.userChoice;
				if (choiceResult.outcome === 'accepted') {
					setShowBanner(false);
					setDeferredPrompt(null);
				}
			} catch (err) {
				console.error('Error during PWA installation:', err);
			}
		} else if (isIos) {
			setShowBanner(false);
			setShowIosGuide(true);
		}
	};

	const handleDismiss = () => {
		setShowBanner(false);
		localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now() + THREE_DAYS_MS));
	};

	return (
		<>
			{/* Pull To Refresh Animated Indicator */}
			{pullDistance > 0 && (
				<div
					style={{ transform: `translateY(${pullDistance}px)` }}
					className="fixed top-2 left-1/2 -translate-x-1/2 z-50 transition-transform duration-75 flex items-center gap-2 bg-slate-900/90 text-white px-3.5 py-1.5 rounded-full shadow-lg text-xs backdrop-blur-md"
				>
					<RotateCw
						className={`size-3.5 text-emerald-400 ${pullDistance > 65 ? 'animate-spin' : ''}`}
						style={{ transform: `rotate(${pullDistance * 4}deg)` }}
					/>
					<span>{pullDistance > 65 ? 'Release to refresh' : 'Pull down to refresh'}</span>
				</div>
			)}

			{children}

			{/* Floating Refresh Button in PWA Standalone Mode */}
			{isStandalone && (
				<div className="fixed bottom-6 right-6 z-40 animate-in fade-in zoom-in-95 duration-200">
					<Button
						variant="outline"
						size="sm"
						onClick={triggerRefresh}
						disabled={isRefreshing}
						className="h-10 px-3.5 rounded-full shadow-xl bg-slate-900/90 hover:bg-slate-800 text-white border-slate-700/80 backdrop-blur-md gap-2"
						title="Refresh app & scan data"
					>
						<RotateCw className={`size-4 text-emerald-400 ${isRefreshing ? 'animate-spin' : ''}`} />
						<span className="text-xs font-semibold">Refresh</span>
					</Button>
				</div>
			)}

			{/* Floating Install App Banner */}
			{showBanner && !isStandalone && (
				<aside
					aria-label="Install App"
					className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 sm:max-w-md w-auto animate-in slide-in-from-bottom-5 duration-300"
				>
					<div className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-2xl backdrop-blur-md">
						<button
							onClick={handleDismiss}
							aria-label="Close install prompt"
							className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-full transition-colors cursor-pointer"
						>
							<X className="size-4" />
						</button>

						<div className="flex items-start gap-3.5 pr-6">
							<div className="size-11 rounded-xl bg-white p-1 flex items-center justify-center shrink-0 border border-emerald-500/20 shadow-xs">
								<Image
									src="/maskable-icon-192x192.png"
									alt="Ekutu QR Logo"
									width={36}
									height={36}
									className="rounded-lg object-contain"
								/>
							</div>

							<div className="flex-1 min-w-0">
								<h2 className="text-sm font-semibold text-slate-900 dark:text-white">
									Install Ekutu QR App
								</h2>
								<p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
									{isIos ? (
										<>
											Add to your iPhone Home Screen for instant camera scanning, fast QR creation,
											and full screen experience.
										</>
									) : (
										'Install for 1-tap barcode scanning, fast QR generation, and offline access.'
									)}
								</p>

								<div className="flex items-center gap-2 mt-3">
									<Button
										size="sm"
										onClick={handleInstallClick}
										className="h-8 px-3.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-xs cursor-pointer"
									>
										<Download className="size-3.5 mr-1.5" />
										{isIos ? 'How to Install' : 'Install App'}
									</Button>
									<Button
										variant="ghost"
										size="sm"
										onClick={handleDismiss}
										className="h-8 px-3 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 cursor-pointer"
									>
										Not now
									</Button>
								</div>
							</div>
						</div>
					</div>
				</aside>
			)}

			{/* iOS Step-by-Step Install Guide Modal */}
			<Dialog open={showIosGuide} onOpenChange={setShowIosGuide}>
				<DialogContent className="sm:max-w-md rounded-2xl">
					<DialogHeader>
						<div className="mx-auto size-14 rounded-2xl bg-white p-2 border border-emerald-500/20 shadow-sm flex items-center justify-center mb-2">
							<Image
								src="/maskable-icon-192x192.png"
								alt="Ekutu QR"
								width={44}
								height={44}
								className="rounded-xl object-contain"
							/>
						</div>
						<DialogTitle className="text-center text-lg font-bold text-slate-900 dark:text-white">
							Install Ekutu QR on iPhone / iPad
						</DialogTitle>
						<DialogDescription className="text-center text-xs text-slate-500 dark:text-slate-400">
							Install in 3 simple taps with zero App Store download:
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-3.5 py-3">
						<div className="flex items-start gap-3 bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
							<div className="flex items-center justify-center size-7 rounded-full bg-emerald-600 text-white font-bold text-xs shrink-0">
								1
							</div>
							<div className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
								Tap the{' '}
								<strong className="font-semibold text-slate-900 dark:text-white">Share</strong>{' '}
								button <Share className="inline size-4 text-emerald-600 align-text-bottom mx-0.5" />{' '}
								in your Safari toolbar (at the bottom or top).
							</div>
						</div>

						<div className="flex items-start gap-3 bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
							<div className="flex items-center justify-center size-7 rounded-full bg-emerald-600 text-white font-bold text-xs shrink-0">
								2
							</div>
							<div className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
								Scroll down and tap{' '}
								<strong className="font-semibold text-slate-900 dark:text-white">
									Add to Home Screen
								</strong>{' '}
								<PlusSquare className="inline size-4 text-emerald-600 align-text-bottom mx-0.5" />.
							</div>
						</div>

						<div className="flex items-start gap-3 bg-slate-50 dark:bg-slate-800/60 p-3 rounded-xl border border-slate-100 dark:border-slate-800">
							<div className="flex items-center justify-center size-7 rounded-full bg-emerald-600 text-white font-bold text-xs shrink-0">
								3
							</div>
							<div className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
								Tap <strong className="font-semibold text-slate-900 dark:text-white">Add</strong> in
								the top-right corner. Ekutu QR will appear right on your Home Screen!
							</div>
						</div>
					</div>

					<div className="pt-1">
						<Button
							className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold h-9"
							onClick={() => setShowIosGuide(false)}
						>
							Got it, thanks!
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}
